import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, DollarSign, Mail, RefreshCw } from "lucide-react";
import { formatTime12Hour } from "@/lib/utils";
import { getStoredTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";

interface CompletedSession {
  id: string;
  scheduled_at: string;
  scheduled_start_time: string;
  duration_minutes: number;
  completed_at: string;
  mentor_payout_amount: number;
  platform_fee_amount: number;
  payout_invoice_sent: boolean;
  credit_value_used: number;
  original_hourly_rate: number;
  invoice_paid: boolean;
  is_discovery_call: boolean;
  mentors: {
    first_name: string;
    last_name: string;
    email: string;
    specialty: string;
  };
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export const CompletedSessionsManager = () => {
  const [sessions, setSessions] = useState<CompletedSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState<string | null>(null);
  const [updatingPaid, setUpdatingPaid] = useState<string | null>(null);
  const { toast } = useToast();

  const loadCompletedSessions = async () => {
    setLoading(true);
    try {
      const { data: sessionsData, error } = await supabase
        .from('sessions')
        .select(`
          *,
          mentors!inner (first_name, last_name, email, specialty),
          profiles!sessions_student_id_fkey (first_name, last_name, email)
        `)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Fetch invoice data for each session
      const sessionsWithInvoices = await Promise.all(
        (sessionsData || []).map(async (session) => {
          const { data: invoice } = await supabase
            .from('mentor_invoices')
            .select('status, paid_at')
            .eq('session_id', session.id)
            .maybeSingle();

          return {
            ...session,
            invoice_paid: invoice?.status === 'paid' || !!invoice?.paid_at
          };
        })
      );

      setSessions(sessionsWithInvoices);
    } catch (error) {
      console.error('Error loading completed sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load completed sessions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async (sessionId: string, isResend: boolean = false) => {
    setSendingInvoice(sessionId);
    try {
      const { data, error } = await supabase.functions.invoke('send-payout-invoice', {
        body: { sessionId }
      });

      if (error) throw error;

      toast({
        title: isResend ? "Invoice Resent!" : "Invoice Sent!",
        description: `Payout invoice has been ${isResend ? 'resent' : 'sent'} to Lance@Blaqout.us`,
      });

      // Refresh the list
      loadCompletedSessions();
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invoice",
        variant: "destructive",
      });
    } finally {
      setSendingInvoice(null);
    }
  };

  const handleTogglePaid = async (sessionId: string, currentPaidStatus: boolean) => {
    setUpdatingPaid(sessionId);
    try {
      // First, check if invoice exists
      const { data: existingInvoice, error: checkError } = await supabase
        .from('mentor_invoices')
        .select('id')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (checkError) throw checkError;

      const newStatus = currentPaidStatus ? 'reviewed' : 'paid';
      const paidAt = currentPaidStatus ? null : new Date().toISOString();

      if (existingInvoice) {
        // Update existing invoice
        const { error: updateError } = await supabase
          .from('mentor_invoices')
          .update({
            status: newStatus,
            paid_at: paidAt,
          })
          .eq('session_id', sessionId);

        if (updateError) throw updateError;
      } else {
        // Create invoice if it doesn't exist
        const { error: createError } = await supabase.rpc('create_mentor_invoice', {
          p_session_id: sessionId,
          p_mentor_notes: null
        });

        if (createError) throw createError;

        // Then update it to paid
        const { error: updateError } = await supabase
          .from('mentor_invoices')
          .update({
            status: newStatus,
            paid_at: paidAt,
          })
          .eq('session_id', sessionId);

        if (updateError) throw updateError;
      }

      toast({
        title: currentPaidStatus ? "Marked as Unpaid" : "Marked as Paid",
        description: currentPaidStatus 
          ? "Invoice has been marked as unpaid" 
          : "Invoice has been marked as paid for your records",
      });

      // Refresh the list
      loadCompletedSessions();
    } catch (error: any) {
      console.error('Error toggling paid status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update paid status",
        variant: "destructive",
      });
    } finally {
      setUpdatingPaid(null);
    }
  };

  useEffect(() => {
    loadCompletedSessions();
  }, []);

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-energy flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Completed Sessions & Invoices
          </CardTitle>
          <Button 
            onClick={loadCompletedSessions} 
            disabled={loading}
            size="sm"
            variant="outline"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Manage completed sessions and send payout invoices
        </p>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {loading ? "Loading completed sessions..." : "No completed sessions found"}
          </div>
        ) : (
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {sessions.map((session) => (
              <div key={session.id} className="border border-border rounded-lg p-4 space-y-3">
                {/* Session Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">
                      {session.profiles?.first_name} {session.profiles?.last_name} → {session.mentors?.first_name} {session.mentors?.last_name}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Completed: {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: getStoredTimezone() }).format(new Date(session.completed_at))} {getTimezoneAbbr(getStoredTimezone())}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!session.is_discovery_call && (
                      <>
                        <Badge variant={session.payout_invoice_sent ? "default" : "secondary"}>
                          {session.payout_invoice_sent ? "Invoice Sent" : "No Invoice"}
                        </Badge>
                        <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-md">
                          <Checkbox
                            id={`paid-${session.id}`}
                            checked={session.invoice_paid}
                            onCheckedChange={() => handleTogglePaid(session.id, session.invoice_paid)}
                            disabled={updatingPaid === session.id}
                          />
                          <label
                            htmlFor={`paid-${session.id}`}
                            className="text-sm font-medium cursor-pointer select-none"
                          >
                            Paid
                          </label>
                        </div>
                      </>
                    )}
                    {session.is_discovery_call && (
                      <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
                        Discovery Call (FREE)
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Payout Details */}
                <div className="bg-muted/30 rounded-md p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Session ID:</span>
                    <span className="font-mono text-xs">{session.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span>{session.duration_minutes} minutes</span>
                  </div>
                  {session.is_discovery_call ? (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md p-3 my-2">
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                        🎉 Discovery Call (FREE)
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                        No payment or invoice required for discovery calls
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Credit Value Used:</span>
                        <span>${((session.credit_value_used || 0) / 100).toFixed(2)}/credit</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Mentor's Hourly Rate:</span>
                        <span>${((session.original_hourly_rate || 0) / 100).toFixed(2)}/hr</span>
                      </div>
                      <div className="border-t border-border pt-2 mt-2">
                        <div className="flex justify-between font-medium">
                          <span className="text-muted-foreground">Platform Fee (7.5%):</span>
                          <span>${(session.platform_fee_amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-green-600">
                          <span>Mentor Payout (92.5%):</span>
                          <span>${(session.mentor_payout_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Contact Info */}
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Mentor: {session.mentors?.email}</p>
                  <p>Student: {session.profiles?.email}</p>
                </div>

                {/* Actions */}
                {!session.is_discovery_call && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSendInvoice(session.id, session.payout_invoice_sent)}
                      disabled={sendingInvoice === session.id}
                      size="sm"
                      variant={session.payout_invoice_sent ? "outline" : "default"}
                    >
                      {sendingInvoice === session.id ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          {session.payout_invoice_sent ? "Resend Invoice" : "Send Invoice"}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
